/* Rotates the homepage hero images. Progressive enhancement: without JS the first slide stays visible. */
if (!customElements.get('hero-rotator')) {
  customElements.define(
    'hero-rotator',
    class HeroRotator extends HTMLElement {
      connectedCallback() {
        this.slides = Array.from(this.querySelectorAll('[data-hm-slide]'));
        this.dots = Array.from(this.querySelectorAll('[data-hm-dot]'));
        /* One slot per slide, same order — index i belongs to slide i. Empty for slides with no button. */
        this.ctas = Array.from(this.querySelectorAll('[data-hm-cta]'));
        this.ctaRow = this.querySelector('[data-hm-cta-row]');
        this.index = 0;
        this.timer = null;

        if (this.slides.length < 2) return;

        this.interval = Math.max(1000, parseInt(this.dataset.interval, 10) || 5000);
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        /* canAutorotate is the section setting and never changes; autorotate is the live state, which the closing
           slide switches off. Keeping the two apart is what lets a dot click put a finished hero back in motion. */
        this.canAutorotate = this.dataset.autorotate === 'true' && !this.reducedMotion;
        this.autorotate = this.canAutorotate;

        /* Deliberately no mouseenter pause: the hero spans the page, so a resting cursor would park it forever.
           Keyboard focus still pauses, which is what someone tabbing through the dots needs. */
        this.onFocusIn = () => this.stop();
        this.onFocusOut = () => this.start();
        this.onVisibilityChange = () => (document.hidden ? this.stop() : this.start());

        this.addEventListener('focusin', this.onFocusIn);
        this.addEventListener('focusout', this.onFocusOut);
        document.addEventListener('visibilitychange', this.onVisibilityChange);

        /* The button row is the one narrow strip where hover pause is worth it: without it the slide can turn
           over mid-reach and the shopper clicks a link they never chose. Restricted to real hover pointers —
           a tap fires mouseenter too, but the matching mouseleave waits for a tap somewhere else, so on a phone
           this would read as the hero freezing for no reason. */
        if (this.ctaRow && window.matchMedia('(hover: hover)').matches) {
          this.ctaRow.addEventListener('mouseenter', this.onFocusIn);
          this.ctaRow.addEventListener('mouseleave', this.onFocusOut);
        }

        this.dots.forEach((dot, i) => {
          dot.addEventListener('click', () => {
            /* Asking for a slide by hand is a request to keep browsing, so re-arm the live state first — otherwise
               a single click after the closing slide has ended the sequence strands the shopper on a hero that
               never advances again, with only the inner list still moving to suggest anything is alive. */
            this.autorotate = this.canAutorotate;
            this.stop();
            this.show(i);
            this.start();
          });
        });

        this.start();
      }

      disconnectedCallback() {
        this.stop();
        document.removeEventListener('visibilitychange', this.onVisibilityChange);
      }

      /* A slide may hold longer than the shared interval — the animated slides ask for one full pass through their list. */
      currentDuration() {
        const declared = parseInt(this.slides[this.index].dataset.duration, 10);
        return declared > 0 ? declared : this.interval;
      }

      activeList() {
        return this.slides[this.index].querySelector('[data-cc-answers]');
      }

      /* The sequence is over — hold this slide and stop advancing. */
      finish() {
        this.autorotate = false;
        if (this.timer) {
          clearTimeout(this.timer);
          this.timer = null;
        }
      }

      /* Moved on by a slide that has finished saying its piece, rather than by the shared timer. */
      advance() {
        if (!this.autorotate) return;
        if (this.timer) {
          clearTimeout(this.timer);
          this.timer = null;
        }
        this.show(this.index + 1);
        this.start();
      }

      start() {
        const list = this.activeList();
        if (list) ccRun(list);

        /* A closing slide runs to the end of its own list and then stops, so it must not also carry a hero
           timer — the two would otherwise expire in the same tick and race to decide what happens next. */
        if (list && list.dataset.ccStop === 'true') return;

        /* A rolling list times itself: it is only done once the last row has cleared the top, which no
           number written in the theme editor can know. It calls advance() when it gets there, so a timer
           here could only ever cut the roll short or leave an empty window waiting for it. */
        if (list && list.ccRolling) return;

        if (!this.autorotate || this.timer || document.hidden) return;
        this.timer = setTimeout(() => {
          this.timer = null;
          this.show(this.index + 1);
          this.start();
        }, this.currentDuration());
      }

      /* Pausing has to freeze the inner list too, or the slide keeps animating while the hero sits still. */
      stop() {
        const list = this.activeList();
        if (list) ccStop(list);

        if (!this.timer) return;
        clearTimeout(this.timer);
        this.timer = null;
      }

      show(next) {
        const total = this.slides.length;
        this.index = ((next % total) + total) % total;

        this.slides.forEach((slide, i) => slide.classList.toggle('is-active', i === this.index));
        this.ctas.forEach((cta, i) => cta.classList.toggle('is-active', i === this.index));
        this.syncAnswers();
        this.dots.forEach((dot, i) => {
          const current = i === this.index;
          dot.classList.toggle('is-active', current);
          if (current) {
            dot.setAttribute('aria-current', 'true');
          } else {
            dot.removeAttribute('aria-current');
          }
        });
      }

      /* Run an animated slide's list only while that slide is on screen, always from the top. */
      syncAnswers() {
        this.slides.forEach((slide, i) => {
          const list = slide.querySelector('[data-cc-answers]');
          if (!list) return;
          if (i === this.index) {
            ccReset(list);
          } else {
            ccStop(list);
          }
        });
      }
    }
  );
}

function ccItems(list) {
  return {
    answers: Array.from(list.querySelectorAll('[data-cc-answer]')),
    dots: Array.from(list.parentElement.querySelectorAll('[data-cc-dot]')),
  };
}

function ccPaint(list, previous) {
  const { answers, dots } = ccItems(list);
  answers.forEach((answer, i) => {
    answer.classList.toggle('is-active', i === list.ccActive);
    answer.classList.toggle('is-leaving', i === previous);
  });
  dots.forEach((dot, i) => dot.classList.toggle('is-active', i === list.ccActive));
}

/* Credit-roll mode: the whole list rides up as one block instead of items swapping in place. */
function ccIsRoll(list) {
  return list.dataset.ccScroll === 'true';
}

function ccTrack(list) {
  return list.querySelector('[data-cc-track]');
}

/* Distance and duration come from the rows as actually rendered, so wrapped lines and any font size
   the merchant picks all stay at one steady reading speed: "seconds per list item" is the time a row
   takes to travel its own height. The roll starts with the first row resting on the bottom edge and
   ends once the last one has cleared the top. */
function ccRollStart(list) {
  const track = ccTrack(list);
  if (!track) return false;
  const rows = track.children.length;
  const windowH = list.clientHeight;
  const trackH = track.scrollHeight;
  const firstH = track.firstElementChild ? track.firstElementChild.offsetHeight : 0;
  if (!rows || !windowH || !trackH) return false;

  const step = Math.max(400, parseInt(list.dataset.ccInterval, 10) || 1500);
  const from = Math.max(0, windowH - firstH);
  const duration = Math.round(((from + trackH) / (trackH / rows)) * step);

  track.style.setProperty('--hm-roll-from', from + 'px');
  track.style.setProperty('--hm-roll-to', -trackH + 'px');
  track.style.animationDuration = duration + 'ms';
  track.style.animationPlayState = 'running';
  track.classList.add('is-rolling');
  return true;
}

function ccRollRun(list) {
  const track = ccTrack(list);
  if (!track) return;
  if (!track.dataset.ccBound) {
    track.dataset.ccBound = 'true';
    track.addEventListener('animationend', () => ccRollEnd(list));
  }
  /* Already part-way up — this is a resume after a pause, so pick up where it stopped. */
  if (track.classList.contains('is-rolling')) {
    track.style.animationPlayState = 'running';
    list.ccRolling = true;
    return;
  }
  if (ccRollStart(list)) {
    list.ccRetried = false;
    list.ccRolling = true;
    return;
  }
  /* Nothing measurable. On the closing slide the old fallback was to declare the list played out, which
     is the one outcome a shopper must never see by accident — the whole point of the slide is the roll.
     Give the layout one more frame first; only a list that still cannot be measured skips to the end. */
  if (list.dataset.ccStop === 'true' && !list.ccRetried) {
    list.ccRetried = true;
    requestAnimationFrame(() => ccRollRun(list));
    return;
  }
  list.ccRetried = false;
  if (list.dataset.ccStop === 'true') ccFinish(list);
}

/* The roll has run its course. On the closing slide that ends the sequence. On any other slide the roll
   is simply how that slide times itself, so the hero moves straight on rather than sitting on a window
   the list has already left; and if there is nowhere to move on to, it rolls again. */
function ccRollEnd(list) {
  if (list.dataset.ccStop === 'true') {
    ccFinish(list);
    return;
  }
  list.ccRolling = false;
  const rotator = list.closest('hero-rotator');
  if (rotator && rotator.autorotate && typeof rotator.advance === 'function') {
    rotator.advance();
    return;
  }
  ccReset(list);
}

function ccStop(list) {
  if (ccIsRoll(list)) {
    const track = ccTrack(list);
    if (track) track.style.animationPlayState = 'paused';
    return;
  }
  if (!list.ccTimer) return;
  clearInterval(list.ccTimer);
  list.ccTimer = null;
}

/* End of the road: stop the hero, let the last item sit for its hold, then hand the stage to the buttons. */
function ccFinish(list) {
  ccStop(list);
  list.ccDone = true;
  const rotator = list.closest('hero-rotator');
  if (rotator && typeof rotator.finish === 'function') rotator.finish();

  const slide = list.closest('[data-hm-slide]');
  if (!slide) return;
  const hold = Math.max(0, parseInt(list.dataset.ccHold, 10) || 0);
  /* Deliberately not cleared by ccStop: pausing partway through the hold would otherwise strand the
     slide on a finished list with no buttons. Only ccReset, which starts the list over, cancels it. */
  clearTimeout(list.ccHandoff);
  list.ccHandoff = setTimeout(() => slide.classList.add('is-finished'), hold);
}

/* Resume from wherever the list is — used when the hero un-pauses. */
function ccRun(list) {
  ccStop(list);
  if (list.ccDone) return;

  const stopAtEnd = list.dataset.ccStop === 'true';
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Cleared up front: a list that fails to roll, for whatever reason, has to fall back to the hero's
     own timer rather than leave the slide waiting for an animation that will never end. */
  list.ccRolling = false;

  if (ccIsRoll(list)) {
    if (reduced) {
      /* No motion allowed: the list simply stands still and the buttons follow after the hold. */
      list.classList.add('is-static');
      if (stopAtEnd) ccFinish(list);
      return;
    }
    ccRollRun(list);
    return;
  }

  const total = ccItems(list).answers.length;

  if (reduced) {
    /* Nothing will animate, so a closing slide would otherwise sit there forever with no buttons. */
    if (stopAtEnd) ccFinish(list);
    return;
  }
  if (total < 2) {
    if (stopAtEnd) ccFinish(list);
    return;
  }

  const step = Math.max(400, parseInt(list.dataset.ccInterval, 10) || 1500);
  list.ccTimer = setInterval(() => {
    const items = ccItems(list).answers.length;
    const previous = list.ccActive || 0;
    if (stopAtEnd && previous >= items - 1) {
      ccFinish(list);
      return;
    }
    list.ccActive = (previous + 1) % items;
    ccPaint(list, previous);
  }, step);
}

/* Taking is-finished off is not instant: the list window eases back up from zero height over half a
   second, the label unfolds and the buttons fade away. The roll is measured off that window moments
   later, and a height still on its way up reads as a window with no room for a single row — so the
   slide gave up and showed its ending again, which is what a second visit to the closing slide looked
   like. Suspending the transitions for one frame puts the pieces back where they started in one go,
   so the measurement that follows is the real one. */
function ccRewind(slide) {
  if (!slide.classList.contains('is-finished')) return;
  slide.classList.add('is-rewinding');
  slide.classList.remove('is-finished');
  void slide.offsetWidth;
  slide.classList.remove('is-rewinding');
}

/* Start the list from the top — used when a slide comes around. */
function ccReset(list) {
  clearTimeout(list.ccHandoff);
  list.ccDone = false;
  const slide = list.closest('[data-hm-slide]');
  if (slide) ccRewind(slide);

  if (ccIsRoll(list)) {
    const track = ccTrack(list);
    if (track) {
      /* Dropping the class and reading a layout property forces the animation to be applied afresh,
         otherwise re-adding it in the same frame just carries on from where it left off. */
      track.classList.remove('is-rolling');
      track.style.animationPlayState = '';
      void track.offsetWidth;
    }
    ccRun(list);
    return;
  }

  list.ccActive = 0;
  ccPaint(list, -1);
  ccRun(list);
}

/* If an animated slide is the one showing on load, the rotator may never fire — kick it off here. */
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.homepage-modern__slide.is-active [data-cc-answers]').forEach((list) => {
    if (!list.ccTimer) ccReset(list);
  });
});
